import { memo, useEffect, useState, ReactElement } from 'react';
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import { Tooltip } from '../common';
import { HSVtoRGB, lerp, RGBColor } from '../../utils';

// Types
interface ProbePoint {
  index: number | string;
  x: number | string;
  y: number | string;
  z: number | string;
}

interface CalibrationData {
  z_min?: number | string;
  z_max?: number | string;
  z_deviation?: number | string;
  z_median?: number | string;
  z_max_distance_to_median?: number | string;
  probe_points?: ProbePoint[][];
}

interface BedMeshHeightMapProps {
  calibration: CalibrationData | null;
  index?: number;
}

interface TableRowData {
  key: string;
  value: string;
}

// Constants
const BED_DIMENSIONS = { x: 250, y: 350, x_offset: 50, y_offset: 50 };
const BED_OFFSET = 5;
const TABLE_DATA = ['z_min', 'z_max', 'z_deviation', 'z_median', 'z_max_distance_to_median'];
const xOffset = BED_DIMENSIONS.x_offset / 2;
const yOffset = BED_DIMENSIONS.y_offset / 2;

// Components
const Background = ({ rectClassName }: { rectClassName: string }) => (
  <>
    <rect
      x={BED_DIMENSIONS.x_offset / 2}
      y={BED_DIMENSIONS.y_offset / 2}
      width={BED_DIMENSIONS.x}
      height={BED_DIMENSIONS.y}
      className={rectClassName}
    />
    <rect
      x={BED_DIMENSIONS.x / 8 + BED_DIMENSIONS.x_offset / 2}
      y={BED_DIMENSIONS.y + BED_DIMENSIONS.y_offset / 2}
      width={BED_DIMENSIONS.x - BED_DIMENSIONS.x / 4}
      height={15}
      className={rectClassName}
    />
    <text
      x={BED_DIMENSIONS.x + BED_DIMENSIONS.x_offset / 2 - 7}
      y={14}
      dominantBaseline="middle"
      textAnchor="middle"
    >
      X
    </text>
    <text
      x={BED_DIMENSIONS.x + BED_DIMENSIONS.x_offset / 2 + 7}
      y={28}
      dominantBaseline="middle"
      textAnchor="middle"
    >
      Y
    </text>
  </>
);

const createRectangle = ({
  key,
  x,
  y,
  width,
  height,
  color,
}: {
  key: number;
  x: number;
  y: number;
  width: number;
  height: number;
  color: RGBColor;
}): ReactElement => {
  return (
    <rect
      key={key}
      x={x}
      y={y}
      width={width}
      height={height}
      style={{
        fill: `rgb(${color.r},${color.g}, ${color.b})`,
      }}
    />
  );
};

const createText = ({
  key,
  x,
  y,
  point,
}: {
  key: number;
  x: number;
  y: number;
  point: ProbePoint;
}): ReactElement => (
  <Tooltip
    key={key}
    title={
      <Box>
        <Typography variant="caption">Probe: {parseFloat(String(point.index)) + 1}</Typography>
        <br />
        <Typography variant="caption">X: {parseFloat(String(point.x)).toFixed(2)}</Typography>
        <br />
        <Typography variant="caption">Y: {parseFloat(String(point.y)).toFixed(2)}</Typography>
      </Box>
    }
  >
    <text x={x} y={y} dominantBaseline="middle" textAnchor="middle">
      {parseFloat(String(point.z)).toFixed(4)}
    </text>
  </Tooltip>
);

const BedMeshHeightMap = ({ calibration, index }: BedMeshHeightMapProps) => {
  const [loading, setLoading] = useState(true);
  const [rectangles, setRectangles] = useState<ReactElement[]>([]);
  const [tableRows, setTableRows] = useState<TableRowData[]>([]);
  const [texts, setTexts] = useState<ReactElement[]>([]);

  useEffect(() => {
    if (!calibration) return;

    function loadMap() {
      setLoading(true);

      setTimeout(() => {
        const zMin = calibration?.z_min;

        const rects: ReactElement[] = [];
        const txts: ReactElement[] = [];

        const minBedDistance = parseFloat(String(zMin));
        const maxBedDistance = minBedDistance + BED_OFFSET;

        const probePoints = calibration!.probe_points;

        if (!probePoints?.length) return;

        const rows = probePoints.length;
        const columns = probePoints[0]?.length;

        if (rows === undefined || columns === undefined) return;

        const width = BED_DIMENSIONS.x / columns;
        const height = BED_DIMENSIONS.y / rows;

        probePoints.forEach((points: ProbePoint[], row: number) => {
          if (!Array.isArray(points)) return;

          points.reverse().forEach((point, column) => {
            const index = parseFloat(String(point.index));
            const z = parseFloat(String(point.z));

            const percDistance =
              z <= minBedDistance
                ? 0.0
                : z >= maxBedDistance
                  ? 1.0
                  : (z - minBedDistance) / (maxBedDistance - minBedDistance);

            const hue = lerp(0, 130, 1 - percDistance);

            const color = HSVtoRGB({ h: hue, s: 70, v: 90 });

            if (color === null) return;

            const x = column * width + xOffset;
            const y = row * height + yOffset;

            const rectangle = createRectangle({
              key: index,
              x,
              y,
              width,
              height,
              color,
            });
            rects.push(rectangle);

            const text = createText({
              key: index,
              x: x + width / 2,
              y: y + height / 2,
              point,
            });
            txts.push(text);
          });
        });

        const tableData = TABLE_DATA.reduce((accum: TableRowData[], key) => {
          const value = calibration![key as keyof CalibrationData];
          if (value !== undefined && value !== null) {
            accum.push({
              key,
              value: parseFloat(String(value)).toFixed(4),
            });
          }
          return accum;
        }, []);

        setTexts(txts);
        setRectangles(rects);
        setTableRows(tableData);
        setLoading(false);
      }, 100);
    }

    loadMap();
  }, [calibration]);

  if (!calibration) return null;

  return (
    <>
      <Typography variant="subtitle2">
        Bed Height Map
        {index != null ? ` ${index + 1}` : ''}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        {loading ? (
          <Box>
            <svg
              width={BED_DIMENSIONS.x + BED_DIMENSIONS.x_offset}
              height={BED_DIMENSIONS.y + BED_DIMENSIONS.y_offset}
            >
              <Background rectClassName={'rectangle-loading'} />
            </svg>
          </Box>
        ) : null}
        {!loading ? (
          <>
            <Box sx={{ mr: 3 }}>
              <svg
                width={BED_DIMENSIONS.x + BED_DIMENSIONS.x_offset}
                height={BED_DIMENSIONS.y + BED_DIMENSIONS.y_offset}
              >
                <Background rectClassName={'rect-background'} />
                {rectangles}
                {texts}
              </svg>
            </Box>
            <Box>
              <TableContainer component={Paper}>
                <Table size="small" aria-label="a dense table">
                  <TableBody>
                    {tableRows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell component="th" scope="row">
                          {row.key}
                        </TableCell>
                        <TableCell align="center">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        ) : null}
      </Box>
    </>
  );
};

export default memo(BedMeshHeightMap);
